import { z } from "zod";
import {
  AUTOCOMPLETE_MAX_PREFIX_CHARS,
  AUTOCOMPLETE_MAX_SUFFIX_CHARS,
} from "@racler/shared";
import { effortSchema, modelIdSchema, providerIdSchema, usageSchema, uuidSchema } from "./aiTypes.ts";
import { request } from "./defs.ts";
import { projectIdSchema, relativePathSchema } from "./schemas.ts";

/** Cursor position in Monaco (1-based line and column). */
export const cursorPositionSchema = z.strictObject({
  lineNumber: z.number().int().positive(),
  column: z.number().int().positive(),
});
export type CursorPosition = z.infer<typeof cursorPositionSchema>;

/**
 * Autocomplete request payload (browser -> server).
 * Identity fields (requestId, modelVersion, cursorPosition) allow latest-request-wins validation.
 */
export const autocompleteRequestSchema = z.strictObject({
  requestId: uuidSchema,
  projectId: projectIdSchema,
  path: relativePathSchema,
  language: z.string().min(1).max(64),
  modelVersion: z.number().int().nonnegative(),
  cursorPosition: cursorPositionSchema,
  prefix: z.string().max(AUTOCOMPLETE_MAX_PREFIX_CHARS * 2),
  suffix: z.string().max(AUTOCOMPLETE_MAX_SUFFIX_CHARS * 2),
});
export type AutocompleteRequest = z.infer<typeof autocompleteRequestSchema>;

/** Autocomplete response payload (server -> browser). */
export const autocompleteResponseSchema = z.strictObject({
  requestId: uuidSchema,
  text: z.string().max(32_000),
  unavailable: z.boolean().optional(),
  reason: z.string().max(500).optional(),
});
export type AutocompleteResponse = z.infer<typeof autocompleteResponseSchema>;

/** Autocomplete cancellation request payload (browser -> server). */
export const autocompleteCancelSchema = z.strictObject({
  requestId: uuidSchema,
});
export type AutocompleteCancel = z.infer<typeof autocompleteCancelSchema>;

export const autocompleteCancelResponseSchema = z.strictObject({
  cancelled: z.boolean(),
});
export type AutocompleteCancelResponse = z.infer<typeof autocompleteCancelResponseSchema>;

/**
 * Server -> Agent autocomplete command payload.
 * The server resolves the authoritative Autocomplete model default and passes provider credentials.
 */
export const agentAutocompleteCommandSchema = z.strictObject({
  requestId: uuidSchema,
  projectId: projectIdSchema,
  path: relativePathSchema,
  language: z.string().min(1).max(64),
  prefix: z.string().max(AUTOCOMPLETE_MAX_PREFIX_CHARS * 2),
  suffix: z.string().max(AUTOCOMPLETE_MAX_SUFFIX_CHARS * 2),
  provider: providerIdSchema,
  model: modelIdSchema,
  effort: effortSchema.optional(),
  credentialId: z.string().min(1).max(64).optional(),
});
export type AgentAutocompleteCommand = z.infer<typeof agentAutocompleteCommandSchema>;

/** Agent -> Server autocomplete command response. */
export const agentAutocompleteResponseSchema = z.strictObject({
  requestId: uuidSchema,
  text: z.string().max(32_000),
  usage: usageSchema.optional(),
});
export type AgentAutocompleteResponse = z.infer<typeof agentAutocompleteResponseSchema>;

export const autocompleteRequests = {
  "agent/ai.autocomplete": request(
    "agent",
    "ai.autocomplete",
    autocompleteRequestSchema,
    autocompleteResponseSchema,
  ),
  "agent/ai.autocomplete.cancel": request(
    "agent",
    "ai.autocomplete.cancel",
    autocompleteCancelSchema,
    autocompleteCancelResponseSchema,
  ),
} as const;

export const autocompleteCommands = {
  "agent/ai.autocomplete": request(
    "agent",
    "ai.autocomplete",
    agentAutocompleteCommandSchema,
    agentAutocompleteResponseSchema,
  ),
  "agent/ai.autocomplete.cancel": request(
    "agent",
    "ai.autocomplete.cancel",
    autocompleteCancelSchema,
    autocompleteCancelResponseSchema,
  ),
} as const;
